// MobileStep.js — Mortgage Matrix Broker CRM
// v9.4.8 — login-aware branch lookup + direct screen-pop receiver support
// ---------------------------------------------------------------------------------------------------------------
// ✅ Uses CURRENT URL params / CRM metadata / passed branchContext as source of truth
// ✅ Sends opportunityId / contactId / crmRef when present
// ✅ Preserves branch metadata on not-found / errors
// ✅ Preserves extra routing fields: branchScope / virtualBranch / tokenKey
// ✅ Debug logs show exact payload used by the app
// ✅ Manual Next does NOT advance if lookup fails
// ✅ FIX: contact-only Mercury matches are NOT treated as existing opportunities
// ✅ FIX: leadFound is only true when an actual opportunity exists
// ✅ FIX: removed Baljot forced fallback so admin can use main branch properly
// ✅ FIX: admin/main branch can intentionally send blank branchKey/tokenKey with branchScope=admin
// ✅ NEW: hydrates dependantAges + numberOfDependents/numberOfDependants
// ✅ NEW: hydrates DOB aliases more safely
// ✅ NEW: listens for direct extension screen-pop events without requiring full page reload

import React, { useState, useEffect, useMemo, useRef } from "react"
import { useLocation } from "react-router-dom"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile"

// -------------------------------------------------------------
// Normalise AU mobile
// -------------------------------------------------------------
const normaliseAU = (n = "") => {
  let x = String(n || "").replace(/\D/g, "")
  if (x.startsWith("61")) x = "0" + x.slice(2)
  if (!x.startsWith("0")) x = "0" + x
  return x.slice(0, 10)
}

// -------------------------------------------------------------
// Relationship status normaliser
// -------------------------------------------------------------
const normaliseRelationshipStatus = (obj) => {
  const v = obj?.relationshipStatus ?? obj?.maritalStatus ?? obj?.marital_status ?? obj?.relationship ?? ""
  return String(v || "").trim()
}

// -------------------------------------------------------------
// Extract DOB raw value from common keys + nested shapes
// -------------------------------------------------------------
const pickDobRaw = (obj) => {
  if (!obj) return undefined

  const direct =
    obj.dateOfBirth ??
    obj.date_of_birth ??
    obj.birthDate ??
    obj.birth_date ??
    obj.dob ??
    obj.DOB ??
    obj.dobDate ??
    obj.dob_date ??
    obj.dateOfBirthUtc ??
    obj.dateOfBirthUTC ??
    obj.dateOfBirthEpoch ??
    obj.dateOfBirthMs ??
    obj.dateOfBirthMillis ??
    obj.dateOfBirthSeconds ??
    obj.dobEpoch ??
    obj.dobMs ??
    obj.dobMillis ??
    obj.dobSeconds

  if (direct !== undefined && direct !== null && direct !== "") return direct

  const nested =
    obj.contact?.dateOfBirth ??
    obj.contact?.dob ??
    obj.contact?.birthDate ??
    obj.person?.dateOfBirth ??
    obj.person?.dob ??
    obj.details?.dateOfBirth ??
    obj.details?.dob ??
    obj.profile?.dateOfBirth ??
    obj.profile?.dob

  if (nested !== undefined && nested !== null && nested !== "") return nested

  if (typeof obj.dateOfBirth === "object" && obj.dateOfBirth?.value) return obj.dateOfBirth.value
  if (typeof obj.dob === "object" && obj.dob?.value) return obj.dob.value

  return undefined
}

// -------------------------------------------------------------
// Convert DOB → yyyy-mm-dd
// -------------------------------------------------------------
const parseDob = (raw) => {
  if (raw === null || raw === undefined || raw === "") return ""

  if (typeof raw === "object") {
    const inner = raw.value ?? raw.dob ?? raw.dateOfBirth ?? raw.birthDate
    if (inner !== undefined) return parseDob(inner)
    return ""
  }

  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return raw.trim()

  if (typeof raw === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw.trim())) {
    const [d, m, y] = raw.trim().split("/")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string" && /^\d{1,2}-\d{1,2}-\d{4}$/.test(raw.trim())) {
    const [d, m, y] = raw.trim().split("-")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string" && /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(raw.trim())) {
    const [y, m, d] = raw.trim().split("/")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string") {
    const s = raw.trim()
    const m = s.match(/\/Date\((\d+)\)\//i)
    if (m?.[1]) {
      const ms = Number(m[1])
      if (Number.isFinite(ms)) {
        const d = new Date(ms)
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
      }
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10)

    const dt = new Date(s)
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10)
  }

  const n = Number(raw)
  if (!Number.isFinite(n)) return ""

  const ms = n < 1e12 ? n * 1000 : n
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ""
  return d.toISOString().slice(0, 10)
}

// -------------------------------------------------------------
// Transaction Type normaliser
// -------------------------------------------------------------
const normaliseTranxType = (v) => {
  const s = String(v || "").trim().toLowerCase()
  if (!s) return ""
  if (s.includes("refi")) return "Refinance"
  if (s.includes("purch") || s.includes("buy")) return "Purchase"
  return String(v || "").trim()
}

const getDesiredTranxTypeFromCrm = (crmData) => {
  const raw =
    crmData?.property?.loanPurpose ||
    crmData?.property?.transactionType ||
    crmData?.property?.tranxType ||
    crmData?.needsObjectives?.loanPurpose ||
    crmData?.metadata?.loanPurpose ||
    crmData?.metadata?.transactionType ||
    crmData?.transactionType ||
    ""
  return normaliseTranxType(raw)
}

const applyTranxTypeToCrm = (updateCrmData, tranxType) => {
  const t = normaliseTranxType(tranxType)
  if (!t) return
  updateCrmData("property", { loanPurpose: t, transactionType: t, tranxType: t })
  updateCrmData("metadata", { suggestedTransactionType: t, transactionType: t })
}

// -------------------------------------------------------------
// Fill blanks only
// -------------------------------------------------------------
const isEmpty = (v) => {
  if (v === null || v === undefined) return true

  if (typeof v === "object") {
    const formatted = v?.formatted || v?.label || v?.formatted_address || v?.address || v?.description
    return String(formatted || "").trim() === ""
  }

  return String(v).trim() === ""
}

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) if (!isEmpty(v)) return v
  return ""
}

const safeHydrateProperty = (currentProperty, updateCrmData, source = {}) => {
  if (!source || typeof source !== "object") return

  const current = currentProperty || {}

  const srcAddress = pickFirstNonEmpty(source.propertyAddress, source.address, source.property_address)
  const srcValue = pickFirstNonEmpty(
    source.propertyValue,
    source.value,
    source.property_value,
    source.estimatedValue,
    source.estimated_value
  )
  const srcLoanBal = pickFirstNonEmpty(
    source.loanBalance,
    source.currentLoanBalance,
    source.balance,
    source.current_balance,
    source.loan_balance
  )
  const srcRate = pickFirstNonEmpty(
    source.interestRate,
    source.rate,
    source.interest_rate,
    source.currentRate,
    source.current_rate
  )
  const srcLender = pickFirstNonEmpty(
    source.currentLender,
    source.lender,
    source.current_lender,
    source.creditor
  )

  const srcPurchasePrice = pickFirstNonEmpty(
    source.purchasePrice,
    source.purchase_price,
    source.price,
    source.propertyPrice,
    source.property_price
  )
  const srcDepositAmount = pickFirstNonEmpty(source.depositAmount, source.deposit, source.deposit_amount)
  const srcFoundProperty = pickFirstNonEmpty(
    source.foundProperty,
    source.hasFoundProperty,
    source.hasFound,
    source.found_property,
    source.has_found_property
  )
  const srcFirstHomeBuyer = pickFirstNonEmpty(
    source.isFirstHomeBuyer,
    source.firstHomeBuyer,
    source.fhb,
    source.first_home_buyer
  )
  const srcOccupancy = pickFirstNonEmpty(source.occupancy, source.propertyUse, source.use, source.property_use)

  const nextPatch = {
    propertyAddress: isEmpty(current.propertyAddress) ? srcAddress : undefined,
    propertyValue: isEmpty(current.propertyValue) ? srcValue : undefined,
    loanBalance: isEmpty(current.loanBalance) ? srcLoanBal : undefined,
    interestRate: isEmpty(current.interestRate) ? srcRate : undefined,
    currentLender: isEmpty(current.currentLender) ? srcLender : undefined,

    purchasePrice: isEmpty(current.purchasePrice) ? srcPurchasePrice : undefined,
    depositAmount: isEmpty(current.depositAmount) ? srcDepositAmount : undefined,
    foundProperty: isEmpty(current.foundProperty) ? srcFoundProperty : undefined,
    isFirstHomeBuyer: isEmpty(current.isFirstHomeBuyer) ? srcFirstHomeBuyer : undefined,
    occupancy: isEmpty(current.occupancy) ? srcOccupancy : undefined,
  }

  Object.keys(nextPatch).forEach((k) => nextPatch[k] === undefined && delete nextPatch[k])

  if (Object.keys(nextPatch).length) {
    updateCrmData("property", nextPatch)
  }
}

// -------------------------------------------------------------
// URL-first resolvers
// -------------------------------------------------------------
const getLiveUrlParams = () => {
  try {
    return new URLSearchParams(window.location.search || "")
  } catch {
    return new URLSearchParams("")
  }
}

const resolveBranchContext = (branchContext = {}, crmData = {}, locationSearch = "") => {
  const params = new URLSearchParams(locationSearch || window.location.search || "")
  const live = getLiveUrlParams()
  const meta = crmData?.metadata || {}

  return {
    branchKey:
      live.get("branchKey") ||
      live.get("virtualBranch") ||
      live.get("brokerKey") ||
      live.get("branch") ||
      params.get("branchKey") ||
      params.get("virtualBranch") ||
      params.get("brokerKey") ||
      params.get("branch") ||
      meta?.branchKey ||
      meta?.virtualBranchKey ||
      meta?.brokerKey ||
      meta?.branch ||
      branchContext?.branchKey ||
      branchContext?.branch ||
      "",

    branchScope:
      live.get("branchScope") ||
      params.get("branchScope") ||
      meta?.branchScope ||
      branchContext?.branchScope ||
      "",

    virtualBranch:
      live.get("virtualBranch") ||
      params.get("virtualBranch") ||
      meta?.virtualBranch ||
      branchContext?.virtualBranch ||
      "",

    tokenKey:
      live.get("tokenKey") ||
      live.get("branchTokenKey") ||
      params.get("tokenKey") ||
      params.get("branchTokenKey") ||
      meta?.tokenKey ||
      meta?.branchTokenKey ||
      branchContext?.tokenKey ||
      branchContext?.branchTokenKey ||
      "",

    brokerPersonId:
      live.get("brokerPersonId") ||
      live.get("brokerId") ||
      params.get("brokerPersonId") ||
      params.get("brokerId") ||
      meta?.brokerPersonId ||
      meta?.brokerId ||
      branchContext?.brokerPersonId ||
      "",

    brokerCA:
      live.get("brokerCA") ||
      live.get("ca") ||
      params.get("brokerCA") ||
      params.get("ca") ||
      meta?.brokerCA ||
      meta?.ca ||
      branchContext?.brokerCA ||
      "",

    branchName:
      live.get("branchName") ||
      live.get("brokerName") ||
      params.get("branchName") ||
      params.get("brokerName") ||
      meta?.branchName ||
      meta?.brokerName ||
      branchContext?.branchName ||
      "",
  }
}

const resolveIdentifiers = (crmData = {}, locationSearch = "") => {
  const params = new URLSearchParams(locationSearch || window.location.search || "")
  const live = getLiveUrlParams()
  const meta = crmData?.metadata || {}

  return {
    opportunityId:
      live.get("opportunityId") ||
      live.get("oppId") ||
      live.get("mercuryOpportunityId") ||
      params.get("opportunityId") ||
      params.get("oppId") ||
      params.get("mercuryOpportunityId") ||
      meta?.mercuryOpportunityId ||
      meta?.opportunityId ||
      "",

    contactId:
      live.get("contactId") ||
      live.get("mercuryContactId") ||
      params.get("contactId") ||
      params.get("mercuryContactId") ||
      meta?.mercuryContactId ||
      meta?.contactId ||
      "",

    crmRef:
      live.get("crmRef") ||
      live.get("leadRef") ||
      params.get("crmRef") ||
      params.get("leadRef") ||
      meta?.crmRef ||
      meta?.leadRef ||
      meta?.ref ||
      "",
  }
}

function MobileStep({ crmData, updateCrmData, next, branchContext = {} }) {
  const [loading, setLoading] = useState(false)
  const [mobileInput, setMobileInput] = useState("")

  const location = useLocation()
  const lastAutoMobileRef = useRef("")
  const crmDataRef = useRef(crmData)

  useEffect(() => {
    crmDataRef.current = crmData
  }, [crmData])

  useEffect(() => {
    const existingMobile = crmData?.mobile?.mobile
    if (existingMobile && !mobileInput) setMobileInput(existingMobile)
  }, [crmData, mobileInput])

  const ui = useMemo(() => {
    const ACCENT = "#4EDCF6"
    const DARK = "#111827"
    const MUTED = "#6B7280"
    const BORDER = "#E5E7EB"
    const INPUT_BORDER = "#334155"
    const CARD_BG = "#E6FBFF"
    const SHADOW = "0 10px 24px rgba(78,220,246,0.22)"

    return {
      page: { padding: 8, maxWidth: 920 },
      h2: { marginBottom: 10, fontSize: 26, fontWeight: 700, color: DARK },
      intro: { marginBottom: 12, color: MUTED, fontWeight: 500, lineHeight: 1.4 },
      card: { background: CARD_BG, padding: 18, borderRadius: 18, border: `3px solid ${ACCENT}`, boxShadow: SHADOW },
      inner: { background: "#fff", padding: 16, borderRadius: 16, border: `2px solid ${BORDER}` },
      label: { display: "block", marginBottom: 8, fontSize: 15, fontWeight: 600, color: DARK },
      input: {
        width: "100%",
        height: 50,
        padding: "10px 12px",
        borderRadius: 14,
        border: `2px solid ${INPUT_BORDER}`,
        background: "#fff",
        fontSize: 16,
        fontWeight: 600,
        outline: "none",
      },
      hint: { marginTop: 10, fontSize: 13, color: MUTED, fontWeight: 500 },
      btnRow: { marginTop: 14, display: "flex", gap: 12, alignItems: "center" },
      primaryBtn: {
        padding: "12px 18px",
        background: ACCENT,
        border: "none",
        borderRadius: 12,
        cursor: "pointer",
        fontWeight: 600,
        boxShadow: SHADOW,
      },
      disabled: { opacity: 0.7, cursor: "not-allowed" },
    }
  }, [])

  const fetchExisting = async (mobile04) => {
    try {
      const desiredTransactionType = getDesiredTranxTypeFromCrm(crmDataRef.current)
      const resolvedBranch = resolveBranchContext(branchContext, crmDataRef.current, location.search)
      const identifiers = resolveIdentifiers(crmDataRef.current, location.search)

      const isAdminMainBranch =
        String(resolvedBranch.branchScope || "").toLowerCase() === "admin" &&
        !String(resolvedBranch.branchKey || "").trim() &&
        !String(resolvedBranch.virtualBranch || "").trim() &&
        !String(resolvedBranch.tokenKey || "").trim()

      const payload = {
        mobile: mobile04,
        ...(desiredTransactionType ? { desiredTransactionType } : {}),
        ...(identifiers.opportunityId ? { opportunityId: identifiers.opportunityId } : {}),
        ...(identifiers.contactId ? { contactId: identifiers.contactId } : {}),
        ...(identifiers.crmRef ? { crmRef: identifiers.crmRef } : {}),
        ...(resolvedBranch.branchKey ? { branchKey: resolvedBranch.branchKey } : {}),
        ...(resolvedBranch.virtualBranch ? { virtualBranch: resolvedBranch.virtualBranch } : {}),
        ...(resolvedBranch.tokenKey ? { tokenKey: resolvedBranch.tokenKey } : {}),
        ...(resolvedBranch.brokerPersonId ? { brokerPersonId: resolvedBranch.brokerPersonId } : {}),
        ...(resolvedBranch.brokerCA ? { brokerCA: resolvedBranch.brokerCA } : {}),
        ...(resolvedBranch.branchName ? { branchName: resolvedBranch.branchName } : {}),
        ...((resolvedBranch.branchScope || isAdminMainBranch) ? { branchScope: resolvedBranch.branchScope || "admin" } : {}),
      }

      console.log("APP LOOKUP PAYLOAD", {
        ...payload,
        resolvedBranchFinal: resolvedBranch,
        isAdminMainBranch,
        liveSearch: window.location.search,
        metadata: crmDataRef.current?.metadata || {},
      })

      const resp = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      const text = await resp.text()
      let data = {}
      try {
        data = JSON.parse(text)
      } catch {
        console.error("LOOKUP INVALID JSON", text)
        toast.error("Invalid Mercury response.")
        return { ok: false }
      }

      if (data?.body && typeof data.body === "string") {
        try {
          data = JSON.parse(data.body)
        } catch {
          // keep outer object
        }
      }

      const contactFound = !!(data?.found || data?.contactId)

      const hasExistingOpportunity = !!(
        data?.hasExistingOpportunity ||
        data?.opportunityId ||
        data?.resume?.resumeOpportunityId ||
        data?.resumeOpportunityId
      )

      const effectiveFound = hasExistingOpportunity

      console.log("APP LOOKUP RESPONSE", {
        success: data?.success,
        foundRaw: data?.found,
        contactFound,
        hasExistingOpportunity,
        effectiveFound,
        contactId: data?.contactId || "",
        opportunityId: data?.opportunityId || "",
        resumeOpportunityId: data?.resume?.resumeOpportunityId || data?.resumeOpportunityId || "",
        branchContext: data?.branchContext || {},
        branchKey: data?.branchKey || "",
        branchScope: data?.branchScope || "",
        virtualBranch: data?.virtualBranch || "",
        tokenKey: data?.tokenKey || data?.branchTokenKey || "",
        dependantAges: data?.dependantAges || [],
        coApplicantDependantAges: data?.coApplicant?.dependantAges || [],
      })

      updateCrmData("mobile", { mobile: mobile04 })

      if (!data.success) {
        toast.error("Mercury lookup failed.")
        updateCrmData("metadata", {
          mercuryContactId: "",
          mercuryOpportunityId: "",
          leadFound: false,
          ...(identifiers.crmRef ? { crmRef: identifiers.crmRef } : {}),
          ...(resolvedBranch.branchKey ? { branchKey: resolvedBranch.branchKey } : {}),
          ...(resolvedBranch.branchScope ? { branchScope: resolvedBranch.branchScope } : {}),
          ...(resolvedBranch.virtualBranch ? { virtualBranch: resolvedBranch.virtualBranch } : {}),
          ...(resolvedBranch.tokenKey ? { tokenKey: resolvedBranch.tokenKey } : {}),
          ...(resolvedBranch.brokerPersonId ? { brokerPersonId: resolvedBranch.brokerPersonId } : {}),
          ...(resolvedBranch.brokerCA ? { brokerCA: resolvedBranch.brokerCA } : {}),
          ...(resolvedBranch.branchName ? { branchName: resolvedBranch.branchName } : {}),
        })
        return { ok: false }
      }

      const apiBranchContext = data?.branchContext || {}
      const finalBranch = {
        branchKey:
          data.branchKey ||
          apiBranchContext.branchKey ||
          resolvedBranch.branchKey ||
          "",

        branchScope:
          data.branchScope ||
          apiBranchContext.branchScope ||
          resolvedBranch.branchScope ||
          "",

        virtualBranch:
          data.virtualBranch ||
          apiBranchContext.virtualBranch ||
          resolvedBranch.virtualBranch ||
          "",

        tokenKey:
          data.tokenKey ||
          data.branchTokenKey ||
          apiBranchContext.tokenKey ||
          apiBranchContext.branchTokenKey ||
          resolvedBranch.tokenKey ||
          "",

        brokerPersonId:
          apiBranchContext.brokerPersonId ||
          data.brokerPersonId ||
          resolvedBranch.brokerPersonId ||
          "",

        brokerCA:
          apiBranchContext.brokerCA ||
          data.brokerCA ||
          resolvedBranch.brokerCA ||
          "",

        branchName:
          apiBranchContext.branchName ||
          data.branchName ||
          resolvedBranch.branchName ||
          "",
      }

      const suggestedT = normaliseTranxType(
        data.suggestedTransactionType || data.transactionType || data.tranxType || ""
      )
      if (suggestedT) applyTranxTypeToCrm(updateCrmData, suggestedT)

      if (data.uiDraft) updateCrmData("metadata", { uiDraft: data.uiDraft })
      if (data.propertyStep) updateCrmData("metadata", { propertyStep: data.propertyStep })
      if (data.assignedBroker) updateCrmData("metadata", { assignedBroker: data.assignedBroker })

      if (!effectiveFound) {
        toast.info(
          contactFound
            ? "Contact found, but no existing opportunity matched this branch. You can continue — lead will be created on submission."
            : "No existing Mercury record found. You can continue — lead will be created on submission."
        )

        updateCrmData("metadata", {
          mercuryContactId: data.contactId || identifiers.contactId || "",
          mercuryOpportunityId: "",
          leadFound: false,
          ...(identifiers.crmRef ? { crmRef: identifiers.crmRef } : {}),
          ...(finalBranch.branchKey ? { branchKey: finalBranch.branchKey } : {}),
          ...(finalBranch.branchScope ? { branchScope: finalBranch.branchScope } : {}),
          ...(finalBranch.virtualBranch ? { virtualBranch: finalBranch.virtualBranch } : {}),
          ...(finalBranch.tokenKey ? { tokenKey: finalBranch.tokenKey } : {}),
          ...(finalBranch.brokerPersonId ? { brokerPersonId: finalBranch.brokerPersonId } : {}),
          ...(finalBranch.brokerCA ? { brokerCA: finalBranch.brokerCA } : {}),
          ...(finalBranch.branchName ? { branchName: finalBranch.branchName } : {}),
        })

        updateCrmData("applicants", [
          {
            id: 1,
            contactId: data.contactId || identifiers.contactId || "",
            title: data.title || "",
            firstName: data.firstName || "",
            middleName: "",
            lastName: data.lastName || "",
            dob: parseDob(pickDobRaw(data)),
            dateOfBirth: parseDob(pickDobRaw(data)),
            birthDate: parseDob(pickDobRaw(data)),
            gender: data.gender || "",
            dependants: data.dependants ?? data.numberOfDependents ?? data.numberOfDependants ?? "",
            dependantAges: Array.isArray(data.dependantAges) ? data.dependantAges : [],
            numberOfDependents:
              data.numberOfDependents ?? data.numberOfDependants ?? data.dependants ?? "",
            numberOfDependants:
              data.numberOfDependants ?? data.numberOfDependents ?? data.dependants ?? "",
            mobile: mobile04,
            email: data.email || "",
            relationshipStatus: normaliseRelationshipStatus(data),
            relationship: normaliseRelationshipStatus(data),
            residency: data.residency || "",
            creditHistory: data.creditHistory || "",
          },
        ])

        updateCrmData("income", [])
        updateCrmData("assetsLiabilities", {
          assets: [],
          liabilities: [],
          mortgageLoans: [],
          creditCards: [],
          personalLoans: [],
          carLoans: [],
          hecs: [],
        })
        updateCrmData("expenses", [])

        updateCrmData("property", {
          propertyAddress: "",
          propertyValue: "",
          loanBalance: "",
          interestRate: "",
          currentLender: "",
          purchasePrice: "",
          depositAmount: "",
          foundProperty: "",
          isFirstHomeBuyer: "",
          occupancy: "",
          ...(suggestedT ? { loanPurpose: suggestedT, transactionType: suggestedT, tranxType: suggestedT } : {}),
        })

        return { ok: true, found: false, data }
      }

      toast.success("Match found in Mercury!")

      const a1DobRaw = pickDobRaw(data)
      const a1Dob = parseDob(a1DobRaw)
      const a1Rel = normaliseRelationshipStatus(data)

      const applicantsArr = [
        {
          id: 1,
          contactId: data.contactId || identifiers.contactId || "",
          title: data.title || "",
          firstName: data.firstName || "",
          middleName: "",
          lastName: data.lastName || "",
          dob: a1Dob,
          dateOfBirth: a1Dob,
          birthDate: a1Dob,
          gender: data.gender || "",
          dependants: data.dependants ?? data.numberOfDependents ?? data.numberOfDependants ?? "",
          dependantAges: Array.isArray(data.dependantAges) ? data.dependantAges : [],
          numberOfDependents:
            data.numberOfDependents ?? data.numberOfDependants ?? data.dependants ?? "",
          numberOfDependants:
            data.numberOfDependants ?? data.numberOfDependents ?? data.dependants ?? "",
          mobile: mobile04,
          email: data.email || "",
          relationshipStatus: a1Rel,
          relationship: a1Rel,
          residency: data.residency || "",
          creditHistory: data.creditHistory || "",
        },
      ]

      if (data.coApplicant) {
        const a2DobRaw = pickDobRaw(data.coApplicant)
        const a2Dob = parseDob(a2DobRaw)
        const a2Rel = normaliseRelationshipStatus(data.coApplicant)

        applicantsArr.push({
          id: 2,
          contactId: data.coApplicant.contactId || "",
          title: data.coApplicant.title || "",
          firstName: data.coApplicant.firstName || "",
          middleName: "",
          lastName: data.coApplicant.lastName || "",
          dob: a2Dob,
          dateOfBirth: a2Dob,
          birthDate: a2Dob,
          gender: data.coApplicant.gender || "",
          dependants:
            data.coApplicant.dependants ??
            data.coApplicant.numberOfDependents ??
            data.coApplicant.numberOfDependants ??
            "",
          dependantAges: Array.isArray(data.coApplicant.dependantAges) ? data.coApplicant.dependantAges : [],
          numberOfDependents:
            data.coApplicant.numberOfDependents ??
            data.coApplicant.numberOfDependants ??
            data.coApplicant.dependants ??
            "",
          numberOfDependants:
            data.coApplicant.numberOfDependants ??
            data.coApplicant.numberOfDependents ??
            data.coApplicant.dependants ??
            "",
          mobile: data.coApplicant.mobile || "",
          email: data.coApplicant.email || "",
          relationshipStatus: a2Rel,
          relationship: a2Rel,
          residency: data.coApplicant.residency || "",
          creditHistory: data.coApplicant.creditHistory || "",
        })
      }

      updateCrmData("applicants", applicantsArr)

      const basePropertyPatch = {
        propertyAddress: data.propertyAddress || "",
        propertyValue: data.propertyValue || "",
        loanBalance: data.loanBalance || "",
        interestRate: data.interestRate || "",
        currentLender: data.currentLender || "",
        ...(suggestedT ? { loanPurpose: suggestedT, transactionType: suggestedT, tranxType: suggestedT } : {}),
      }

      updateCrmData("property", basePropertyPatch)

      const currentProperty = { ...(crmDataRef.current?.property || {}), ...basePropertyPatch }
      safeHydrateProperty(currentProperty, updateCrmData, data)
      safeHydrateProperty(currentProperty, updateCrmData, data.propertyStep || {})
      safeHydrateProperty(currentProperty, updateCrmData, data.uiDraft || {})

      const incomeList = []
      ;(data.employmentIncomes || []).forEach((entry) => {
        incomeList.push({
          id: `a1-${entry.uniqueId || Math.random().toString(16).slice(2)}`,
          owner: "Applicant 1",
          incomeType: "Employment",
          employerName: entry.employerName || "",
          roleDescription: entry.jobTitle || "",
          category: "Base Salary",
          startDate: "",
          amount: entry.amount || "",
          frequency: "Annual",
        })
      })

      ;(data.coApplicant?.employmentIncomes || []).forEach((entry) => {
        incomeList.push({
          id: `a2-${entry.uniqueId || Math.random().toString(16).slice(2)}`,
          owner: "Applicant 2",
          incomeType: "Employment",
          employerName: entry.employerName || "",
          roleDescription: entry.jobTitle || "",
          category: "Base Salary",
          startDate: "",
          amount: entry.amount || "",
          frequency: "Annual",
        })
      })

      ;(data.otherIncomes || []).forEach((entry, idx) => {
        incomeList.push({
          id: `o-${idx}-${Math.random().toString(16).slice(2)}`,
          owner: "Applicant 1",
          incomeType: entry.type || "Other",
          employerName: "",
          roleDescription: entry.type || "Other Income",
          category: "Other",
          startDate: "",
          amount: entry.amount || "",
          frequency: "Annual",
        })
      })
      updateCrmData("income", incomeList)

      const prevAL = crmDataRef.current?.assetsLiabilities || {}
      const prevAssets = Array.isArray(prevAL.assets) ? prevAL.assets : []
      const prevLiabs = Array.isArray(prevAL.liabilities) ? prevAL.liabilities : []

      updateCrmData("assetsLiabilities", {
        assets: prevAssets,
        liabilities: prevLiabs,
        mortgageLoans: data.mortgageLoans || prevAL.mortgageLoans || [],
        creditCards: data.creditCards || prevAL.creditCards || [],
        personalLoans: data.personalLoans || prevAL.personalLoans || [],
        carLoans: data.carLoans || prevAL.carLoans || [],
        hecs: data.hecs || prevAL.hecs || [],
      })

      const living = []
      ;(data.livingExpenses || []).forEach((exp) => {
        living.push({
          type: exp.type || exp.category || "",
          amount: exp.amount || "",
          split: exp.splits?.[0]?.percent || 0,
        })
      })
      updateCrmData("expenses", living)

      const resumeOppId = data?.resume?.resumeOpportunityId || data?.resumeOpportunityId || ""
      const finalOppId = resumeOppId || data.opportunityId || identifiers.opportunityId || ""

      updateCrmData("metadata", {
        mercuryContactId: data.contactId || identifiers.contactId || "",
        mercuryOpportunityId: finalOppId,
        leadFound: !!finalOppId,
        resumedLead: !!resumeOppId,
        suggestedTransactionType: suggestedT || "",
        ...(identifiers.crmRef ? { crmRef: identifiers.crmRef } : {}),
        branchKey: finalBranch.branchKey,
        branchScope: finalBranch.branchScope,
        virtualBranch: finalBranch.virtualBranch,
        tokenKey: finalBranch.tokenKey,
        brokerPersonId: finalBranch.brokerPersonId,
        brokerCA: finalBranch.brokerCA,
        branchName: finalBranch.branchName,
      })

      console.log("LOOKUP SUCCESS -> FINAL METADATA", {
        mercuryContactId: data.contactId || identifiers.contactId || "",
        mercuryOpportunityId: finalOppId,
        leadFound: !!finalOppId,
        branchKey: finalBranch.branchKey,
        branchScope: finalBranch.branchScope,
        virtualBranch: finalBranch.virtualBranch,
        tokenKey: finalBranch.tokenKey,
      })

      if (resumeOppId) {
        toast.info("Resuming existing Lead (no duplicate created).")
      }

      return { ok: true, found: true, data }
    } catch (err) {
      console.error("LOOKUP EXCEPTION", err)
      const preservedBranch = resolveBranchContext(branchContext, crmDataRef.current, location.search)
      const identifiers = resolveIdentifiers(crmDataRef.current, location.search)

      toast.error("Lookup failed.")
      updateCrmData("metadata", {
        mercuryContactId: "",
        mercuryOpportunityId: "",
        leadFound: false,
        ...(identifiers.crmRef ? { crmRef: identifiers.crmRef } : {}),
        ...(preservedBranch.branchKey ? { branchKey: preservedBranch.branchKey } : {}),
        ...(preservedBranch.branchScope ? { branchScope: preservedBranch.branchScope } : {}),
        ...(preservedBranch.virtualBranch ? { virtualBranch: preservedBranch.virtualBranch } : {}),
        ...(preservedBranch.tokenKey ? { tokenKey: preservedBranch.tokenKey } : {}),
        ...(preservedBranch.brokerPersonId ? { brokerPersonId: preservedBranch.brokerPersonId } : {}),
        ...(preservedBranch.brokerCA ? { brokerCA: preservedBranch.brokerCA } : {}),
        ...(preservedBranch.branchName ? { branchName: preservedBranch.branchName } : {}),
      })

      return { ok: false }
    }
  }

  useEffect(() => {
    const params = getLiveUrlParams()
    const mRaw = params.get("mobile") || params.get("m") || ""
    const m = mRaw ? normaliseAU(mRaw) : ""
    const isScreenPop = params.get("screenpop") === "1" || params.get("sp") === "1"

    console.log("AUTO LOOKUP URL CHECK", {
      liveSearch: window.location.search,
      mobileParam: mRaw || "",
      normalisedMobile: m,
      isScreenPop,
      branch: resolveBranchContext(branchContext, crmDataRef.current, window.location.search),
      ids: resolveIdentifiers(crmDataRef.current, window.location.search),
    })

    if (!m) return
    if (lastAutoMobileRef.current === m) return
    lastAutoMobileRef.current = m

    setMobileInput(m)

    ;(async () => {
      setLoading(true)
      await fetchExisting(m)
      setLoading(false)

      try {
        const url = new URL(window.location.href)
        url.searchParams.delete("mobile")
        url.searchParams.delete("m")
        url.searchParams.delete("t")
        url.searchParams.delete("screenpop")
        url.searchParams.delete("sp")
        window.history.replaceState({}, "", url.toString())
      } catch {
        // ignore
      }

      if (isScreenPop) {
        await new Promise((r) => setTimeout(r, 0))
        next()
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, next])

  useEffect(() => {
    const handleScreenPopMobile = async (event) => {
      const mobileRaw = event?.detail?.mobile || ""
      const mobile = mobileRaw ? normaliseAU(mobileRaw) : ""
      const isScreenPop = true

      console.log("DIRECT SCREENPOP EVENT RECEIVED", {
        rawMobile: mobileRaw,
        normalisedMobile: mobile,
        detail: event?.detail || {},
        liveSearch: window.location.search,
      })

      if (!mobile) return
      if (lastAutoMobileRef.current === mobile) return
      lastAutoMobileRef.current = mobile

      setMobileInput(mobile)

      try {
        const url = new URL(window.location.href)
        url.searchParams.set("mobile", mobile)
        url.searchParams.set("t", String(Date.now()))
        url.searchParams.set("screenpop", "1")
        window.history.replaceState({}, "", url.toString())
      } catch {
        // ignore
      }

      setLoading(true)
      const result = await fetchExisting(mobile)
      setLoading(false)

      if (!result?.ok) return

      try {
        const url = new URL(window.location.href)
        url.searchParams.delete("mobile")
        url.searchParams.delete("m")
        url.searchParams.delete("t")
        url.searchParams.delete("screenpop")
        url.searchParams.delete("sp")
        window.history.replaceState({}, "", url.toString())
      } catch {
        // ignore
      }

      if (isScreenPop) {
        await new Promise((r) => setTimeout(r, 0))
        next()
      }
    }

    window.addEventListener("mm-screenpop-mobile", handleScreenPopMobile)

    return () => {
      window.removeEventListener("mm-screenpop-mobile", handleScreenPopMobile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, location.search])

  const handleNext = async () => {
    if (!mobileInput.trim()) {
      toast.error("Enter mobile number.")
      return
    }

    const mobile04 = normaliseAU(mobileInput)

    console.log("MANUAL NEXT CLICK", {
      mobileInput,
      normalisedMobile: mobile04,
      liveSearch: window.location.search,
      branch: resolveBranchContext(branchContext, crmDataRef.current, window.location.search),
      ids: resolveIdentifiers(crmDataRef.current, window.location.search),
    })

    setLoading(true)
    const result = await fetchExisting(mobile04)
    setLoading(false)

    if (!result?.ok) return

    await new Promise((r) => setTimeout(r, 0))
    next()
  }

  return (
    <div style={ui.page}>
      <h2 style={ui.h2}>Mobile Verification</h2>
      <p style={ui.intro}>Enter a mobile number</p>

      <div style={ui.card}>
        <div style={ui.inner}>
          <label style={ui.label}>Mobile</label>
          <input
            type="tel"
            placeholder="04xxxxxxxx"
            maxLength={10}
            value={mobileInput}
            onChange={(e) => setMobileInput(e.target.value)}
            style={ui.input}
            inputMode="numeric"
            autoComplete="tel"
          />

          <div style={ui.hint}>
            We’ll check Mercury for an existing contact. If none is found, we’ll continue and create the lead only when you
            submit.
          </div>

          <div style={ui.btnRow}>
            <button onClick={handleNext} disabled={loading} style={{ ...ui.primaryBtn, ...(loading ? ui.disabled : {}) }}>
              {loading ? "Checking…" : "Next →"}
            </button>
          </div>
        </div>
      </div>

      <ToastContainer position="top-center" autoClose={2000} hideProgressBar />
    </div>
  )
}

export default MobileStep